import React, { FC, Suspense } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies, import/no-internal-modules
import { hot } from 'react-hot-loader/root';
import { Redirect, Switch } from 'react-router';
import { Route } from 'react-router-dom';
import { TwoFactAuthPage } from 'components/pages';
import { RoutePaths } from 'constant';
import { getDevEnv } from 'utils/is-localhost';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { AccountActivatePage } from './account-activate';
import { GoodbyePage, LogoutPage, ImplicitCallback, AddApiKey } from './auth';
import { ForgotPasswordPage } from './forgot-password';
import { LinkActionPage } from './link-action';
import { MagicLinkPage } from './magic-link';
import { MagicLinkGeneratePage } from './magic-link-generate';
import { ProtectedRoutes } from './protected-routes';
import { ResetPasswordPage } from './reset-password';
import { SessionLogin } from './session-login';
import { SetupProfilePage } from './setup-profile';
import { WebForm } from './web-form';

const LazyLogin = lazyWithRetry(() =>
  import('routes/auth/login').then(({ Login }) => ({ default: Login })),
);

const LazyReactQueryDevtools = lazyWithRetry(() =>
  import('react-query/devtools').then(({ ReactQueryDevtools }) => ({
    default: ReactQueryDevtools,
  })),
);

const { isLocal, isCypress } = getDevEnv();

const AppRoutes: FC = () => (
  <Suspense fallback="">
    <Switch>
      {/* Note:: Trailing slash is still a problem in react router.
       So trying a workaround suggested in the react-router community */}
      <Route
        exact
        strict
        path="(.*//+.*)"
        render={({ location }) => (
          <Redirect to={location.pathname.replace(/\/\/+/g, '/')} />
        )}
      />
      <Route path="/implicit/callback" exact component={ImplicitCallback} />
      <Route path={RoutePaths.login} exact>
        <LazyLogin />
      </Route>
      <Route path={RoutePaths.magicLink} exact component={MagicLinkPage} />
      <Route
        path={RoutePaths.newMagicLink}
        exact
        component={MagicLinkGeneratePage}
      />
      <Route
        path={RoutePaths.magicLink2fa}
        exact
        render={(token) => <TwoFactAuthPage token={token} />}
      />
      <Route
        path={RoutePaths.setupProfile}
        exact
        component={SetupProfilePage}
      />
      <Route
        path={RoutePaths.forgotPassword}
        exact
        component={ForgotPasswordPage}
      />
      <Route
        path={RoutePaths.resetPassword}
        exact
        component={ResetPasswordPage}
      />
      <Route path={RoutePaths.logout} component={LogoutPage} />
      <Route path={RoutePaths.goodbye} component={GoodbyePage} />
      <Route path={RoutePaths.linkAction} component={LinkActionPage} />
      <Route path={RoutePaths.activate} component={AccountActivatePage} />
      <Route path={RoutePaths.loginWithSession} component={SessionLogin} />
      <Route path={RoutePaths.webForm} component={WebForm} />
      <Route path={RoutePaths.addApiKey} component={AddApiKey} />

      <ProtectedRoutes />
    </Switch>
    {isLocal && !isCypress ? (
      <Suspense fallback={null}>
        <LazyReactQueryDevtools position="bottom-right" />
      </Suspense>
    ) : null}
  </Suspense>
);

export const Routes = hot(AppRoutes);
